import { useEffect, useState } from "react";
import { useOrganization } from "../../App";
import {
  Webhook,
  WebhookInput,
  webhookService,
} from "../../services/webhookService";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Copy, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { supabase } from "../../../supabase/supabase";

const eventOptions = [
  { id: "call.started", label: "Call Started" },
  { id: "call.ended", label: "Call Ended" },
  { id: "call.missed", label: "Call Missed" },
  { id: "transcript.completed", label: "Transcript Completed" },
  { id: "recording.completed", label: "Recording Completed" },
  { id: "summary.completed", label: "Summary Completed" },
];

export default function WebhooksTab() {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [webhookInput, setWebhookInput] = useState<WebhookInput>({
    url: "",
    description: "",
    isActive: true,
  });
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);

  const { currentOrganization, refreshOrganization } = useOrganization();
  const { toast } = useToast();

  useEffect(() => {
    if (currentOrganization) {
      fetchWebhooks();
    }
  }, [currentOrganization]);

  const fetchWebhooks = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    try {
      const { webhooks } = await webhookService.getWebhooks(
        currentOrganization.id,
      );
      setWebhooks(webhooks);
    } catch (error) {
      console.error("Error fetching webhooks:", error);
      toast({
        title: "Error",
        description: "Failed to load webhooks. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingWebhook(null);
    setWebhookInput({
      url: "",
      description: "",
      isActive: true,
    });
    setSelectedEvents([]);
    setDialogOpen(true);
  };

  const openEditDialog = (webhook: Webhook) => {
    setEditingWebhook(webhook);
    setWebhookInput({
      url: webhook.url,
      description: webhook.description || "",
      isActive: webhook.isActive || true,
      filterTypes: webhook.filterTypes,
    });
    setSelectedEvents(webhook.filterTypes || []);
    setDialogOpen(true);
  };

  const toggleEvent = (eventId: string) => {
    setSelectedEvents((prev) => {
      if (prev.includes(eventId)) {
        return prev.filter((id) => id !== eventId);
      } else {
        return [...prev, eventId];
      }
    });
  };

  const handleSubmit = async () => {
    if (!currentOrganization) return;
    if (!webhookInput.url.trim()) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid webhook URL.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const webhookData = {
        ...webhookInput,
        filterTypes: selectedEvents,
      };

      if (editingWebhook) {
        await webhookService.updateWebhook(
          currentOrganization.id,
          editingWebhook.id,
          webhookData,
        );
        toast({
          title: "Webhook Updated",
          description: "The webhook has been updated successfully.",
        });
      } else {
        await webhookService.createWebhook(currentOrganization.id, webhookData);
        toast({
          title: "Webhook Created",
          description: "The webhook has been created successfully.",
        });
      }

      setDialogOpen(false);
      fetchWebhooks();
    } catch (error) {
      console.error("Error saving webhook:", error);
      toast({
        title: "Error",
        description: "Failed to save webhook. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (webhook: Webhook) => {
    if (!currentOrganization) return;

    if (!confirm("Are you sure you want to delete this webhook?")) {
      return;
    }

    try {
      await webhookService.deleteWebhook(currentOrganization.id, webhook.id);
      toast({
        title: "Webhook Deleted",
        description: "The webhook has been deleted successfully.",
      });
      fetchWebhooks();
    } catch (error) {
      console.error("Error deleting webhook:", error);
      toast({
        title: "Error",
        description: "Failed to delete webhook. Please try again.",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "Copied to clipboard!",
    });
  };

  const generateApiKey = async () => {
    if (!currentOrganization) return;

    setIsGeneratingKey(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "generate_organization_api_key",
        {
          body: { organizationId: currentOrganization.id },
        },
      );

      if (error) throw error;

      // Refresh the organization data to get the new API key
      await refreshOrganization();

      toast({
        title: "Success",
        description: "New API key generated successfully",
      });
    } catch (error) {
      console.error("Error generating API key:", error);
      toast({
        title: "Error",
        description: "Failed to generate API key. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingKey(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Organization API Key</CardTitle>
          <CardDescription>
            Use this API key to authenticate your webhook endpoints.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2">
            {currentOrganization?.api_key ? (
              <>
                <code className="relative rounded-none bg-muted px-[0.5rem] py-[0.3rem] font-mono text-sm font-semibold">
                  {currentOrganization.api_key}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(currentOrganization.api_key);
                    toast({
                      title: "Copied!",
                      description: "API key copied to clipboard",
                    });
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <span className="text-muted-foreground">
                  No API key available
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateApiKey}
                  disabled={isGeneratingKey}
                >
                  {isGeneratingKey ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    "Generate Key"
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Webhook Configuration</CardTitle>
          <CardDescription>
            Configure webhooks to receive notifications for the following
            events.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium">Supported Event Types</h3>
              <div className="flex flex-wrap gap-2 mt-2">
                {eventOptions.map((event) => (
                  <span
                    key={event.id}
                    className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                  >
                    {event.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Active Webhooks</CardTitle>
              <CardDescription>
                List of all webhooks configured for your organization.
              </CardDescription>
            </div>
            <Button
              onClick={openCreateDialog}
              variant="default"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Webhook
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : webhooks.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-border rounded-none">
              <h3 className="text-lg font-medium">No webhooks configured</h3>
              <p className="text-muted-foreground max-w-md mx-auto mt-2">
                You haven't set up any webhooks yet. Webhooks allow external
                systems to receive notifications when events occur in your
                organization.
              </p>
              <Button
                onClick={openCreateDialog}
                variant="outline"
                className="mt-4"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create First Webhook
              </Button>
            </div>
          ) : (
            <div>
              <div className="rounded-none border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>URL</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Events</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {webhooks.map((webhook) => (
                      <TableRow key={webhook.id}>
                        <TableCell className="font-mono truncate max-w-[200px]">
                          {webhook.url}
                        </TableCell>
                        <TableCell>
                          {webhook.description || (
                            <span className="text-muted-foreground italic">
                              No description
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`px-2 py-1 rounded-none text-xs ${
                              webhook.isActive
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {webhook.isActive ? "Active" : "Inactive"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(webhook.filterTypes || []).map((eventId) => {
                              const event = eventOptions.find(
                                (e) => e.id === eventId,
                              );
                              return (
                                <span
                                  key={eventId}
                                  className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                                >
                                  {event?.label || eventId}
                                </span>
                              );
                            })}
                            {(!webhook.filterTypes ||
                              webhook.filterTypes.length === 0) && (
                              <span className="text-muted-foreground italic text-xs">
                                All events
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => openEditDialog(webhook)}
                              title="Edit"
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleDelete(webhook)}
                              title="Delete"
                            >
                              <Trash2 size={16} className="text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>
              {editingWebhook ? "Edit Webhook" : "Create Webhook"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="url">Webhook URL *</Label>
              <Input
                id="url"
                value={webhookInput.url}
                onChange={(e) =>
                  setWebhookInput({ ...webhookInput, url: e.target.value })
                }
                placeholder="https://example.com/webhook"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={webhookInput.description || ""}
                onChange={(e) =>
                  setWebhookInput({
                    ...webhookInput,
                    description: e.target.value,
                  })
                }
                placeholder="What is this webhook for?"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={webhookInput.isActive}
                onCheckedChange={(checked) =>
                  setWebhookInput({ ...webhookInput, isActive: checked })
                }
              />
              <Label htmlFor="isActive">Webhook is active</Label>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Event Filters</Label>
              <div className="text-sm text-muted-foreground mb-2">
                Select which events should trigger this webhook. If none are
                selected, all events will be sent.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border rounded-none p-3">
                {eventOptions.map((event) => (
                  <div key={event.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`event-${event.id}`}
                      checked={selectedEvents.includes(event.id)}
                      onChange={() => toggleEvent(event.id)}
                      className="h-4 w-4 rounded-none border-gray-300 text-primary focus:ring-primary"
                    />
                    <label
                      htmlFor={`event-${event.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {event.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  {editingWebhook ? "Updating..." : "Creating..."}
                </>
              ) : editingWebhook ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
